import { Box, Flex, Heading, Tooltip, useColorModeValue } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/shared';
import useCurrentAccount from 'src/hooks/useWalletAddress';
import useMyNFTs from 'src/hooks/useMyNFTs';
import { Pagination } from '../../components';
import { SortingSelector } from '../../features/sorting';
import {
  DefaultLimit,
  GeneSortTypes,
  LandSortTypes,
  MiningToolSortTypes,
  NFTType,
  SlimeSortTypes,
  StandardSortTypes,
} from '../../globals/constants';
import RefreshInventoryButton from './RefreshInventoryButton';

interface InventoryHeaderProps {
  nftType: NFTType;
}

const InventoryHeader = (props: InventoryHeaderProps) => {
  const { nftType } = props;
  const { t } = useTranslation();
  const [totalOfNfts, setTotalOfNfts] = React.useState(0);
  const [totalOfPages, setTotalOfPages] = React.useState(0);
  const [title, setTitle] = React.useState('');
  const [sortType, setSortType] = React.useState(StandardSortTypes);
  const metaMaskWalletAddress = useCurrentAccount();
  const {
    myNFTsQuery: { data: myNfts },
  } = useMyNFTs({
    nftType,
    ownerId: metaMaskWalletAddress,
  });

  const defaultBg = useColorModeValue(Colors.light.secondaryBg, Colors.dark.secondaryBg);

  useEffect(() => {
    const totalNfts = myNfts?.total || 0;
    setTotalOfNfts(totalNfts);
    setTotalOfPages(Math.ceil(totalNfts / DefaultLimit));

    switch (nftType) {
      case NFTType.MINING_TOOL:
        setTitle(t('PageName:MiningTool'));
        setSortType(MiningToolSortTypes);
        break;
      case NFTType.TOOL_MATERIAL:
        setTitle(t('PageName:ToolMaterial'));
        setSortType(StandardSortTypes);
        break;
      case NFTType.LAND:
        setTitle(t('PageName:Land'));
        setSortType(LandSortTypes);
        break;
      case NFTType.GACHA:
        setTitle(t('PageName:Gacha'));
        setSortType(StandardSortTypes);
        break;
      case NFTType.GENE:
        setTitle(t('PageName:Gene'));
        setSortType(GeneSortTypes);
        break;

      case NFTType.SLIME:
        setTitle(t('PageName:Slime'));
        setSortType(SlimeSortTypes);
        break;
    }
  }, [nftType, t, myNfts?.total]);

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      justifyContent={{ base: 'center', lg: 'space-between' }}
      padding={{ base: 5, lg: 0 }}
      alignItems="center"
      position="sticky"
      top={-5}
      background={defaultBg}
      zIndex={8}
    >
      <Box as="span">
        <Heading as="h2" size="lg" color="heading">
          {t(title)} ({totalOfNfts.toLocaleString('en')})
          {metaMaskWalletAddress && (
            <Tooltip label={t('Refresh')} hasArrow>
              <RefreshInventoryButton refreshNFTType={nftType} ownerId={metaMaskWalletAddress} />
            </Tooltip>
          )}
        </Heading>
      </Box>
      <Pagination pageTotal={totalOfPages} />
      <SortingSelector options={sortType} />
    </Flex>
  );
};

export default InventoryHeader;
